// src/pages/ManagePolicies.js
import React, { useState, useEffect, useRef } from "react";

const PRIMARY = "#1F2937";

// ── Default empty question structure ─────────────────────────────────────────
function emptyQuestion() {
  return {
    id:       `pq_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
    question: "",
    options:  [
      { text:"", correct:true  },
      { text:"", correct:false },
      { text:"", correct:false },
      { text:"", correct:false },
    ],
    explanation: "",
  };
}

function PolicyModal({ policy, categories, supervisors, onSave, onClose }) {
  const [tab,        setTab]        = useState("details");
  const [form,       setForm]       = useState({ name: policy?.name||"", categoryId: policy?.categoryId||"", requireAcceptance: policy?.requireAcceptance ?? true });
  const [questions,  setQuestions]  = useState(policy?.questions?.length ? policy.questions : []);
  const [pdfFile,    setPdfFile]    = useState(null);
  const [version,    setVersion]    = useState(policy?.version ? String(policy.version) : "");
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);
  const [uploading,  setUploading]  = useState(false);
  const fileRef = useRef(null);

  const isNew = !policy?.id;

  function setQ(idx, field, val) {
    setQuestions(qs => qs.map((q,i) => i===idx ? {...q,[field]:val} : q));
  }
  function setOpt(qIdx, oIdx, field, val) {
    setQuestions(qs => qs.map((q,i) => i!==qIdx ? q : {
      ...q, options: q.options.map((o,j) => j===oIdx ? {...o,[field]:val} : o),
    }));
  }
  function setCorrect(qIdx, oIdx) {
    setQuestions(qs => qs.map((q,i) => i!==qIdx ? q : {
      ...q, options: q.options.map((o,j) => ({...o, correct: j===oIdx})),
    }));
  }

  async function handleSave() {
    if (!form.name.trim()) { setError("Name required"); setTab("details"); return; }
    if (pdfFile && !version.trim()) { setError("Version number is required when uploading a PDF"); setTab("details"); return; }
    // Validate questions
    for (let i=0; i<questions.length; i++) {
      const q = questions[i];
      if (!q.question.trim()) { setError(`Question ${i+1}: question text is required`); setTab("questions"); return; }
      if (q.options.filter(o=>o.text.trim()).length < 2) { setError(`Question ${i+1}: at least 2 answer options required`); setTab("questions"); return; }
      if (!q.options.some(o=>o.correct)) { setError(`Question ${i+1}: mark one option as correct`); setTab("questions"); return; }
    }
    setSaving(true); setError(null);
    try {
      const url    = isNew ? "/api/policies" : `/api/policies/${policy.id}`;
      const method = isNew ? "POST" : "PUT";
      const body   = { ...form, questions };
      if (version.trim()) body.version = version.trim();
      const res  = await fetch(url, { method, headers:{"Content-Type":"application/json"}, body:JSON.stringify(body) });
      const d    = await res.json();
      if (!res.ok) throw new Error(d.error);
      const saved = d.policy;
      if (pdfFile) {
        setUploading(true);
        const buf = await pdfFile.arrayBuffer();
        const upRes = await fetch(`/api/policies/${saved.id}/pdf`, { method:"POST", body:buf, headers:{"Content-Type":"application/pdf"} });
        if (!upRes.ok) throw new Error((await upRes.json()).error);
        setUploading(false);
      }
      onSave();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); setUploading(false); }
  }

  const TABS = [
    { id:"details",   label:"Details" },
    { id:"questions", label:`Questions ${questions.length>0?`(${questions.length})`:""}` },
  ];

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth:580 }}>
        <div className="modal-header">
          <h2>{isNew ? "Add Policy" : "Edit Policy"}</h2>
          <button className="btn-ghost" onClick={onClose}><svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg></button>
        </div>

        {/* Tabs */}
        <div style={{display:"flex",borderBottom:"1px solid var(--gray-200)",background:"var(--gray-50)"}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{padding:"10px 20px",fontSize:13,fontWeight:tab===t.id?700:400,border:"none",background:"none",
                borderBottom:tab===t.id?"2px solid #1F2937":"2px solid transparent",
                color:tab===t.id?"#1F2937":"var(--gray-500)",cursor:"pointer",transition:"all 0.15s"}}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="modal-body" style={{ overflowY:"auto", maxHeight:"65vh" }}>
          {/* ── Details tab ── */}
          {tab==="details" && <>
            <div className="form-group">
              <label>Policy Name <span style={{color:"var(--red)"}}>*</span></label>
              <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Acceptable Use Policy"/>
            </div>
            <div className="form-row">
              <div className="form-group" style={{margin:0}}>
                <label>Category</label>
                <select value={form.categoryId} onChange={e=>setForm(f=>({...f,categoryId:e.target.value}))}>
                  <option value="">— All users —</option>
                  {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label>Acceptance</label>
                <select value={form.requireAcceptance?"yes":"no"} onChange={e=>setForm(f=>({...f,requireAcceptance:e.target.value==="yes"}))}>
                  <option value="yes">Require acceptance</option>
                  <option value="no">Do not require acceptance</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{margin:0,flex:2}}>
                <label>PDF Document {policy?.pdfBlobPath ? <span style={{fontSize:12,fontWeight:400,color:"#10B981"}}>(PDF uploaded)</span> : null}</label>
                <input type="file" accept="application/pdf" ref={fileRef}
                  onChange={e=>setPdfFile(e.target.files[0]||null)} style={{fontSize:13}}/>
                <div style={{fontSize:12,color:"var(--gray-400)",marginTop:4}}>Max 50MB.</div>
              </div>
              <div className="form-group" style={{margin:0,flex:1}}>
                <label>Version {pdfFile && <span style={{color:"var(--red)"}}>*</span>}</label>
                <input value={version} onChange={e=>setVersion(e.target.value)}
                  placeholder={policy?.pdfBlobPath ? `Current: ${policy.version}` : "e.g. 1.0"}
                  style={{borderColor:pdfFile&&!version.trim()?"var(--red)":undefined}}/>
                <div style={{fontSize:12,color:"var(--gray-400)",marginTop:4}}>
                  {pdfFile?"Required — must match your document.":"Enter when uploading a PDF."}
                </div>
              </div>
            </div>
          </>}

          {/* ── Questions tab ── */}
          {tab==="questions" && <>
            <div style={{fontSize:13,color:"var(--gray-500)",marginBottom:14,lineHeight:1.6}}>
              Add up to <strong>2 multiple choice questions</strong> that users must answer correctly before they can accept the policy. Wrong answers return them to the document to re-read.
            </div>

            {questions.map((q,qi)=>(
              <div key={q.id} style={{border:"1px solid var(--gray-200)",borderRadius:10,padding:"16px",marginBottom:14,background:"var(--gray-50)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <span style={{fontSize:12,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",color:"var(--gray-400)"}}>
                    Question {qi+1}
                  </span>
                  <button className="btn-ghost" style={{fontSize:12,color:"var(--red)",padding:"2px 8px"}}
                    onClick={()=>setQuestions(qs=>qs.filter((_,i)=>i!==qi))}>Remove</button>
                </div>
                <div className="form-group" style={{margin:"0 0 10px 0"}}>
                  <label style={{fontSize:12}}>Question text <span style={{color:"var(--red)"}}>*</span></label>
                  <input value={q.question} onChange={e=>setQ(qi,"question",e.target.value)}
                    placeholder="e.g. What must you do if you suspect a data breach?" style={{fontSize:13}}/>
                </div>
                <div className="form-group" style={{margin:"0 0 8px 0"}}>
                  <label style={{fontSize:12}}>Answer options <span style={{fontWeight:400,color:"var(--gray-400)"}}>— select the correct answer</span></label>
                  {q.options.map((opt,oi)=>(
                    <div key={oi} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                      <input type="radio" name={`correct-${q.id}`} checked={!!opt.correct}
                        onChange={()=>setCorrect(qi,oi)}
                        style={{width:"auto",accentColor:"#10B981",flexShrink:0}}/>
                      <input value={opt.text} onChange={e=>setOpt(qi,oi,"text",e.target.value)}
                        placeholder={`Option ${oi+1}`} style={{flex:1,fontSize:13}}/>
                      {q.options.length>2&&(
                        <button className="btn-ghost" style={{fontSize:12,color:"var(--gray-400)",padding:"2px 6px",flexShrink:0}}
                          onClick={()=>setQ(qi,"options",q.options.filter((_,j)=>j!==oi))}>✕</button>
                      )}
                    </div>
                  ))}
                  {q.options.length<6&&(
                    <button className="btn-secondary" style={{fontSize:12,padding:"3px 10px",marginTop:2}}
                      onClick={()=>setQ(qi,"options",[...q.options,{text:"",correct:false}])}>
                      + Add option
                    </button>
                  )}
                </div>
                <div className="form-group" style={{margin:0}}>
                  <label style={{fontSize:12}}>Explanation <span style={{fontWeight:400,color:"var(--gray-400)"}}>— shown when wrong (optional)</span></label>
                  <input value={q.explanation||""} onChange={e=>setQ(qi,"explanation",e.target.value)}
                    placeholder="e.g. You must report any suspected breach to the IT team immediately." style={{fontSize:13}}/>
                </div>
              </div>
            ))}

            {questions.length<2&&(
              <button className="btn-secondary" style={{width:"100%",fontSize:13}}
                onClick={()=>setQuestions(qs=>[...qs,emptyQuestion()])}>
                + Add Question {questions.length===0?"":"2"}
              </button>
            )}
            {questions.length===0&&(
              <div style={{fontSize:12,color:"var(--gray-400)",textAlign:"center",marginTop:6}}>
                No questions added — users will go straight to the acceptance disclaimer.
              </div>
            )}
          </>}

          {error && <div style={{fontSize:13,color:"var(--red)",marginTop:10,padding:"8px 12px",background:"#FEF2F2",borderRadius:6}}>{error}</div>}
          <div className="form-actions" style={{marginTop:16,position:"sticky",bottom:0,background:"#fff",paddingTop:12,borderTop:"1px solid var(--gray-100)",marginLeft:-24,marginRight:-24,paddingLeft:24,paddingRight:24}}>
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-primary" style={{background:PRIMARY}} onClick={handleSave}
              disabled={saving||uploading||!form.name.trim()}>
              {uploading?"Uploading PDF…":saving?"Saving…":isNew?"Add Policy":"Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResetConfirmModal({ policy, onConfirm, onClose }) {
  return (
    <div className="modal-backdrop">
      <div className="modal" style={{maxWidth:440}}>
        <div className="modal-header"><h2>Reset Acceptance Records?</h2></div>
        <div className="modal-body">
          <p style={{fontSize:14,lineHeight:1.7,color:"var(--gray-700)",marginBottom:16}}>
            A new PDF has been uploaded for <strong>{policy.name}</strong>. Do you want to reset all user acceptances so they must re-read and re-accept the updated policy?
          </p>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <button className="btn-primary" style={{background:PRIMARY}} onClick={()=>onConfirm(true)}>
              Yes — reset all acceptances
            </button>
            <button className="btn-secondary" onClick={()=>onConfirm(false)}>
              No — keep existing acceptances
            </button>
            <button className="btn-ghost" style={{color:"var(--gray-400)"}} onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AcceptanceDrawer({ policy, onClose }) {
  const [acceptances, setAcceptances] = useState([]);
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [resetting,   setResetting]   = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`/api/policies/${policy.id}/acceptance`).then(r=>r.json()),
      fetch("/api/users").then(r=>r.json()),
    ]).then(([a,u]) => {
      setAcceptances(a.acceptances||[]);
      setUsers((u.users||[]).filter(u=>u.active&&!u.deletedAt));
    }).finally(()=>setLoading(false));
  }, [policy.id]);

  async function handleReset() {
    if (!window.confirm("Reset all acceptance records for this policy? All users will need to re-accept.")) return;
    setResetting(true);
    await fetch(`/api/policies/${policy.id}/reset-acceptance`, {method:"POST"});
    setAcceptances([]);
    setResetting(false);
  }

  const accByEmail = new Map(acceptances.map(a=>[a.email,a]));

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{maxWidth:580}}>
        <div className="modal-header">
          <h2>Acceptance — {policy.name}</h2>
          <button className="btn-ghost" onClick={onClose}><svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg></button>
        </div>
        <div className="modal-body">
          {loading ? <div style={{textAlign:"center",padding:20}}><span className="spinner" style={{width:22,height:22}}/></div> : (
            <>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div style={{fontSize:13,color:"var(--gray-500)"}}>
                  {acceptances.length} of {users.length} users have accepted (v{policy.version})
                </div>
                <button className="btn-secondary" style={{fontSize:12,color:"var(--red)"}} onClick={handleReset} disabled={resetting}>
                  {resetting?"Resetting…":"Reset All Acceptances"}
                </button>
              </div>
              <div style={{maxHeight:360,overflowY:"auto",border:"1px solid var(--gray-200)",borderRadius:8}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead>
                    <tr style={{background:"var(--gray-50)"}}>
                      <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,borderBottom:"1px solid var(--gray-200)"}}>User</th>
                      <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,borderBottom:"1px solid var(--gray-200)"}}>Status</th>
                      <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,borderBottom:"1px solid var(--gray-200)"}}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u,i) => {
                      const acc = accByEmail.get(u.email);
                      const current = acc && acc.version === policy.version;
                      return (
                        <tr key={u.email} style={{borderBottom:"1px solid var(--gray-100)",background:i%2?"var(--gray-50)":"#fff"}}>
                          <td style={{padding:"8px 12px"}}>
                            <div style={{fontWeight:600}}>{u.name||u.email}</div>
                            <div style={{fontSize:11,color:"var(--gray-400)"}}>{u.email}</div>
                          </td>
                          <td style={{padding:"8px 12px"}}>
                            {current
                              ? <span style={{color:"#10B981",fontWeight:600}}>✓ Accepted</span>
                              : acc
                                ? <span style={{color:"#F59E0B"}}>⚠ Old version</span>
                                : <span style={{color:"var(--gray-400)"}}>Pending</span>}
                          </td>
                          <td style={{padding:"8px 12px",color:"var(--gray-500)"}}>
                            {acc?.acceptedAt ? new Date(acc.acceptedAt).toLocaleDateString("en-GB") : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ManagePolicies() {
  const [policies,    setPolicies]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState(null); // null | {mode:"new"|"edit", policy?}
  const [acceptance,  setAcceptance]  = useState(null); // policy to show acceptance drawer
  const [viewing,     setViewing]     = useState(null); // policy to preview PDF

  async function load() {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        fetch("/api/policies").then(r=>r.json()),
        fetch("/api/categories").then(r=>r.json()),
      ]);
      setPolicies(p.policies||[]);
      setCategories(c.categories||[]);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function handleDelete(policy) {
    if (!window.confirm(`Delete policy "${policy.name}"? This cannot be undone.`)) return;
    await fetch(`/api/policies/${policy.id}`, {method:"DELETE"});
    load();
  }

  const catName = id => categories.find(c=>c.id===id)?.name || "All users";

  if (loading) return <div style={{display:"flex",justifyContent:"center",padding:60}}><span className="spinner" style={{width:28,height:28}}/></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Manage Policies</div>
          <div className="page-subtitle">{policies.length} polic{policies.length!==1?"ies":"y"}</div>
        </div>
        <button className="btn-primary" style={{background:PRIMARY}} onClick={()=>setModal({mode:"new"})}>
          + Add Policy
        </button>
      </div>

      {policies.length === 0 ? (
        <div className="card" style={{padding:40,textAlign:"center",color:"var(--gray-400)"}}>
          <div style={{fontSize:40,marginBottom:12}}>📋</div>
          <div>No policies yet. Add one to get started.</div>
        </div>
      ) : (
        <div className="card" style={{padding:0,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:"var(--gray-50)"}}>
                {["Policy Name","Category","Acceptance","Version","PDF","Actions"].map(h=>(
                  <th key={h} style={{padding:"10px 14px",textAlign:"left",fontWeight:600,fontSize:12,color:"var(--gray-500)",textTransform:"uppercase",letterSpacing:"0.05em",borderBottom:"1px solid var(--gray-200)"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {policies.map((p,i) => (
                <tr key={p.id} style={{borderBottom:"1px solid var(--gray-100)",background:i%2?"var(--gray-50)":"#fff"}}>
                  <td style={{padding:"12px 14px",fontWeight:600,color:"var(--gray-900)"}}>{p.name}</td>
                  <td style={{padding:"12px 14px",color:"var(--gray-500)"}}>{catName(p.categoryId)}</td>
                  <td style={{padding:"12px 14px"}}>
                    {p.requireAcceptance
                      ? <span style={{fontSize:11,fontWeight:700,color:"#DC2626",background:"#FEE2E2",padding:"2px 8px",borderRadius:99}}>Required</span>
                      : <span style={{fontSize:11,fontWeight:700,color:"var(--gray-400)",background:"var(--gray-100)",padding:"2px 8px",borderRadius:99}}>View only</span>}
                  </td>
                  <td style={{padding:"12px 14px",color:"var(--gray-500)"}}>v{p.version}</td>
                  <td style={{padding:"12px 14px"}}>
                    {p.pdfBlobPath
                      ? <span style={{color:"#10B981",fontWeight:600}}>✓ Uploaded</span>
                      : <span style={{color:"var(--gray-400)"}}>No PDF</span>}
                  </td>
                  <td style={{padding:"12px 14px"}}>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {p.pdfBlobPath && (
                        <button className="btn-secondary" style={{fontSize:12,padding:"4px 10px"}}
                          onClick={()=>setViewing(p)}>Preview</button>
                      )}
                      <button className="btn-secondary" style={{fontSize:12,padding:"4px 10px"}}
                        onClick={()=>setModal({mode:"edit",policy:p})}>Edit</button>
                      {p.requireAcceptance && (
                        <button className="btn-secondary" style={{fontSize:12,padding:"4px 10px"}}
                          onClick={()=>setAcceptance(p)}>Acceptance</button>
                      )}
                      <button className="btn-ghost" style={{fontSize:12,padding:"4px 10px",color:"var(--red)"}}
                        onClick={()=>handleDelete(p)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <PolicyModal
          policy={modal.policy}
          categories={categories}
          onSave={()=>{ setModal(null); load(); }}
          onClose={()=>setModal(null)}
        />
      )}
      {acceptance && (
        <AcceptanceDrawer
          policy={acceptance}
          onClose={()=>{ setAcceptance(null); load(); }}
        />
      )}
      {viewing && (
        <div className="modal-backdrop" style={{zIndex:200}}>
          <div className="modal" style={{maxWidth:720,height:"85vh",display:"flex",flexDirection:"column"}}>
            <div className="modal-header">
              <h2>📄 {viewing.name} — Preview</h2>
              <button className="btn-ghost" onClick={()=>setViewing(null)}><svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg></button>
            </div>
            <iframe src={`/api/policies/${viewing.id}/pdf`} title={viewing.name}
              style={{flex:1,border:"none",background:"#525659"}}/>
          </div>
        </div>
      )}
    </div>
  );
}
